import Button from "@/lib/Button";
import Search from "@/lib/Search";
import ListingPage from "@/lib/layout/ListingPage";
import useModal from "@/lib/modal/useModal";
import {
  useFetchContacts,
  useFetchOrganizations,
  useFetchRfqs,
} from "@/lib/queries/hooks";
import { equalArrays, rfqStatusLabel } from "@/lib/utils";
import { useSearchParams } from "react-router";
import Select from "react-select";
import CreateRFQ from "./CreateRFQ";
import RFQList from "./RFQList";
import { components } from "@/lib/ApiSchema";

export default function RFQs() {
  const { showModal, closeModal } = useModal();
  const contacts = useFetchContacts();
  const { data: organizations } = useFetchOrganizations("");
  const defaultStatusFilters = ["new", "estimated", "quote_created"];

  const [search, setSearch] = useSearchParams({
    status: defaultStatusFilters.join(","),
  });

  const nonDefaultFilters =
    !equalArrays(
      search.get("status")?.split(",").filter(Boolean) ?? [],
      defaultStatusFilters,
    ) ||
    !!search.get("customer") ||
    !!search.get("contact") ||
    !!search.get("search");

  const {
    data: rfqs,
    isLoading,
    isFetching,
  } = useFetchRfqs(
    (search
      .get("status")
      ?.split(",") as components["schemas"]["RFQStatus"][]) || [],
    search.get("customer")?.split(",") || [],
    search.get("contact")?.split(",") || [],
    search.get("search") || "",
  );

  return (
    <ListingPage
      title="RFQ Dashboard"
      fullWidthHeader
      header={
        <div className="flex gap-x-3 gap-y-1 items-center flex-wrap xl:flex-nowrap">
          <div className="grow">
            <Search
              onChange={(value) => {
                if (value) {
                  search.set("search", value);
                } else {
                  search.delete("search");
                }
                setSearch(search);
              }}
              value={search.get("search") || ""}
              isFetching={isFetching}
              className="grow"
              focusable
              autoFocus
            />
          </div>
          <Select
            closeMenuOnSelect={false}
            placeholder="RFQs Status"
            isMulti
            value={(search.get("status") ?? "")
              .split(",")
              .filter(Boolean)
              .map((value) => ({
                value,
                label: `${
                  rfqStatusOptions.find((o) => o.value === value)?.label
                }`,
              }))}
            onChange={(options) => {
              if (options.length > 0) {
                search.set(
                  "status",
                  options.map(({ value }) => value).join(","),
                );
              } else {
                search.delete("status");
              }
              setSearch(search);
            }}
            options={rfqStatusOptions}
            styles={{
              control: (base) => ({
                ...base,
                borderColor: "var(--color-gray-300)",
              }),
            }}
          />
          <Select
            closeMenuOnSelect={false}
            placeholder="Company"
            isMulti
            value={(search.get("customer") ?? "")
              .split(",")
              .filter(Boolean)
              .map((value) => ({
                value,
                label: `${organizations.find((o) => o.id === value)?.name} <${
                  organizations.find((o) => o.id === value)?.email_domain
                }>`,
              }))}
            onChange={(options) => {
              if (options.length > 0) {
                search.set(
                  "customer",
                  options.map(({ value }) => value).join(","),
                );
              } else {
                search.delete("customer");
              }
              setSearch(search);
            }}
            className="min-w-50 max-w-64"
            isSearchable
            options={organizations
              .filter(({ rfq_count }) => rfq_count > 0)
              .map(({ id, name, email_domain }) => ({
                value: id,
                label: `${name} <${email_domain}>`,
              }))}
            styles={{
              control: (base) => ({
                ...base,
                borderColor: "var(--color-gray-300)",
              }),
            }}
          />
          <Select
            closeMenuOnSelect={false}
            placeholder="Contact"
            isMulti
            value={(search.get("contact") ?? "")
              .split(",")
              .filter(Boolean)
              .map((value) => ({
                value,
                label: `${
                  contacts.find((c) => c.email === value)?.name
                } <${value}>`,
              }))}
            onChange={(options) => {
              if (options.length > 0) {
                search.set(
                  "contact",
                  options.map(({ value }) => value).join(","),
                );
              } else {
                search.delete("contact");
              }
              setSearch(search);
            }}
            className="min-w-50 max-w-64"
            isSearchable
            options={contacts.map(({ name, email }) => ({
              value: email,
              label: `${name} <${email}>`,
            }))}
            styles={{
              control: (base) => ({
                ...base,
                borderColor: "var(--color-gray-300)",
              }),
            }}
          />
          {
            <Button
              primary
              onClick={() => showModal(<CreateRFQ done={closeModal} />)}
              className="h-9.5"
            >
              Create RFQ
            </Button>
          }
        </div>
      }
    >
      <RFQList
        rfqs={rfqs}
        isLoading={isLoading}
        nonDefaultFilters={nonDefaultFilters}
      />
    </ListingPage>
  );
}

const rfqStatusOptions = Object.entries(rfqStatusLabel).map(
  ([value, label]) => ({
    label,
    value,
  }),
);
